import {
  buildGumletSrcSet,
  getGumletSizedImageUrl,
  isGumletImageUrl,
} from '@/lib/gumletDeliveryUrl'
import { getGumletPlaceholderUrl } from '@/lib/gumletPlaceholderUrl'
import { FullImage } from './FullImage'
import { PlaceholderImage } from './PlaceholderImage'

export const ProgressiveImage = async ({
  imageUrl,
  alt,
  className = '',
  fetchPriority = 'auto',
  gumletResponsive,
  intrinsicSize,
}: ProgressiveImageProps) => {
  const placeholderUrl = getGumletPlaceholderUrl(imageUrl)

  const widths =
    gumletResponsive?.srcSetWidths &&
    isGumletImageUrl(imageUrl)
      ? gumletResponsive.srcSetWidths
      : undefined
  const maxW =
    widths && widths.length > 0 ? Math.max(...widths) : undefined
  const fullImageSrc =
    maxW !== undefined
      ? getGumletSizedImageUrl(imageUrl, maxW)
      : imageUrl
  const srcSet =
    widths && widths.length > 0
      ? buildGumletSrcSet(imageUrl, [...widths])
      : undefined
  const sizes = srcSet ? gumletResponsive?.sizes : undefined

  // Check if aspect ratio is specified in className
  const hasAspectRatio = /\baspect-/.test(className)
  const objectFitClass = hasAspectRatio ? 'object-cover' : 'object-contain'
  const objectFit = hasAspectRatio ? 'cover' : 'contain'
  const heightClass = hasAspectRatio ? 'h-auto' : 'h-full'

  return (
    <div className={`relative ${className} overflow-hidden`}>
      {/* Placeholder: small image from Gumlet, or grey fallback on load error */}
      <PlaceholderImage
        src={placeholderUrl}
        alt={alt}
        className={`w-full ${heightClass} ${objectFitClass} block`}
      />

      {/* Real image overlays it, fully filling the area */}
      <div className="absolute inset-0 z-10">
        <FullImage
          imageUrl={fullImageSrc}
          alt={alt}
          fetchPriority={fetchPriority}
          objectFit={objectFit}
          srcSet={srcSet}
          sizes={sizes}
          width={intrinsicSize?.width}
          height={intrinsicSize?.height}
        />
      </div>
    </div>
  )
}

interface ProgressiveImageProps {
  imageUrl: string
  alt?: string
  className?: string
  fetchPriority?: 'auto' | 'low' | 'high'
  /** Gumlet-only: responsive srcset + sizes for smaller LCP bytes. */
  gumletResponsive?: {
    srcSetWidths: readonly number[]
    sizes: string
  }
  /** Optional width/height for aspect-ratio hints (Lighthouse unsized-images). */
  intrinsicSize?: { width: number; height: number }
}
